// Embeds a Unicode (Cyrillic-capable) TTF into a jsPDF document so
// pdf.text() can draw Ukrainian labels directly - jsPDF's own 14 built-in
// fonts only cover WinAnsi/Latin-1 and would silently corrupt every
// Cyrillic character. PT Sans (OFL, vendored under src/fonts/pdf/) was
// picked over the app's live UI fonts (Roboto/Public Sans) specifically
// because Google Fonts only ships those two as variable-axis TTFs now -
// jsPDF embeds a font's outlines as-is with no variable-axis instancing,
// so a variable file drawn with style "bold" would still render at its
// single default (regular) weight. PT Sans still ships real static
// Regular/Bold/Italic instances, which is what jsPDF needs.
import ptSansRegularUrl from "../../../fonts/pdf/PTSans-Regular.ttf";
import ptSansBoldUrl from "../../../fonts/pdf/PTSans-Bold.ttf";
import ptSansItalicUrl from "../../../fonts/pdf/PTSans-Italic.ttf";

export const PDF_FONT = "PTSans";

const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};

const fetchBase64 = async (url) => {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    return arrayBufferToBase64(buf);
};

// Fetched once per browser session (the ttf bytes never change) and reused
// across every export in that session instead of re-fetching per PDF.
let cachedFacesPromise = null;
const loadFaces = () => {
    if (!cachedFacesPromise) {
        cachedFacesPromise = Promise.all([
            fetchBase64(ptSansRegularUrl),
            fetchBase64(ptSansBoldUrl),
            fetchBase64(ptSansItalicUrl),
        ]).then(([normal, bold, italic]) => ({ normal, bold, italic }));
    }
    return cachedFacesPromise;
};

// Registers PT Sans (normal/bold/italic) on `pdf` and sets it as the active
// font. Must be awaited before any pdf.text()/drawText() call.
export const registerPdfFonts = async (pdf) => {
    const faces = await loadFaces();
    pdf.addFileToVFS(`${PDF_FONT}-Regular.ttf`, faces.normal);
    pdf.addFont(`${PDF_FONT}-Regular.ttf`, PDF_FONT, "normal");
    pdf.addFileToVFS(`${PDF_FONT}-Bold.ttf`, faces.bold);
    pdf.addFont(`${PDF_FONT}-Bold.ttf`, PDF_FONT, "bold");
    pdf.addFileToVFS(`${PDF_FONT}-Italic.ttf`, faces.italic);
    pdf.addFont(`${PDF_FONT}-Italic.ttf`, PDF_FONT, "italic");
    pdf.setFont(PDF_FONT, "normal");
    // Stashed on the instance so pdfKit's drawText/drawBadge (which don't
    // import this module, to avoid a circular dependency) know which family
    // to re-select before switching weight/style mid-page.
    pdf.__pdfFontName = PDF_FONT;
};

// A throwaway jsPDF instance used only for text-metric calls
// (getTextWidth/splitTextToSize depend on the font+size, not the page
// size) so header measure() calls can get real widths before the actual
// output document's page size is known/created.
let metricsPdfPromise = null;
export const getMetricsPdf = async () => {
    if (!metricsPdfPromise) {
        metricsPdfPromise = (async () => {
            // Dynamic import: jspdf is ~325KB of this bundle's JS for a
            // feature most visits never touch - loaded on demand, right
            // when the user actually exports, instead of shipping it to
            // every page load.
            const { default: jsPDF } = await import("jspdf");
            const pdf = new jsPDF();
            await registerPdfFonts(pdf);
            return pdf;
        })();
    }
    return metricsPdfPromise;
};
